/**
 * The Hand Studio's surface — ADR-022 D1: *point at the tip of each finger and
 * name it.* Everything else on this panel is evidence, pre-fill, or an expert
 * disclosure.
 *
 * WHAT THIS REPLACED. The first version asked the analyzer's question: it listed
 * the candidates the volume had separated and made a person answer each one,
 * including an explicit "not a digit" for every cuff rim and wrist artifact in
 * the scan. It was honest and it worked, and after dozens of real grafts Allen's
 * report was that it is the wrong question — a person does not know which
 * `tip-3` is a finger, they know *that one is the index, right there*. So the
 * candidate rows are gone. The list on this panel is now the person's own pins;
 * the analyzer's candidates are grey ghosts on the model, one click from being
 * adopted, and their numbers are behind a disclosure.
 *
 * The rules the old surface earned are all still here, in their new shape:
 *
 *  - IT SHOWS THE PERSISTENCE PROFILE, NOT THE CLASSIFICATION. On the golden
 *    android the same word `hand-5` meant five real digits on the left hand and
 *    three digits plus two bumps on the right. The reading line still reports
 *    the analyzer's verdict AS an opinion, next to what can contradict it.
 *
 *  - A PROPOSAL NEVER WEARS A PERSON'S CLOTHES (D4). A pre-filled name is
 *    labelled `proposed`, is one click from being changed, and does not lock
 *    (the model draws no ring on it). What it must never look like is an answer
 *    somebody gave.
 *
 *  - RESOLUTION IS A CONTROL, PER HAND — but it no longer gates anything. It
 *    governs what the volume can SHOW, never what a person is allowed to mean.
 *
 * WRITES ARE GATED ON A TRUSTED EVENT (brief §5.6). `confirmedBy` may only be
 * minted from a real user gesture — `event.isTrusted` — so a script driving the
 * DOM cannot sign a confirmation. This is the one place in the app that mints
 * one, and the check lives at the mint, not in a wrapper someone can call
 * around. The pin tool carries the same check at the other door (a forged click
 * cannot even place a dot).
 *
 * A pure resolver: closing it destroys DOM, never data (ADR-015 §13.0.1).
 * Depends on: hands/* (evidence, pins, the gesture's types), store (via mount).
 * Depended on by: main (registration line only).
 */
import type { PanelMount } from './panel-registry.js';
import { attachTip, describeButton, withTip } from './tooltip.js';
import { createDigitFlyout, offeredDigits } from './digit-flyout.js';
import {
  canGraft,
  dispositionOf,
  graftBlockedReason,
  graftHolds,
  setThumbOpposition,
  setWrist,
  wristNudge,
  initialHandUi,
  namingComplete,
  readingSummary,
  setArming,
  setExpected,
  setResolution,
  toggleRecenterWrist,
  withPins,
  type HandUiState,
} from './hands-panel-state.js';
import type { HandCandidate } from '../hands/hand-candidates.js';
import type { HandEvidence } from '../hands/hand-evidence.js';
import {
  addPin,
  adoptCluster,
  namePin,
  reconcileNames,
  namedPins,
  rejectCluster,
  removePin,
  restoreCluster,
  setKnuckle,
  unpinnedClusters,
  witnessPin,
  type HandPin,
} from '../hands/digit-pins.js';
import { faceInRegion, type PinHit } from '../hands/pin-gesture.js';
import { CLUSTER_RADIUS_FRACTION, resolveAtResolution, type TipConsensus } from '../hands/tip-consensus.js';
import { WRIST_MARKER_ID, type GhostMarker, type PinMarker } from '../viewport/hand-markers.js';
import type { DigitName } from '@harryhausen/kernel';
import type { HandLiveReading } from './body-progress.js';

/**
 * The Body header's reading of one hand being pinned (XD note 2026-09-27 §3.1,
 * Hand row): are its names still the pre-fill's proposal, and does any pin
 * stand where the volume saw nothing (the row's `⚠`)? Pure — the panel's own
 * state and sweep, read the way the rows read them.
 */
export function handLiveReading(state: HandUiState, scan: TipConsensus | null): HandLiveReading {
  const pins = state.pins.pins;
  return {
    pins: pins.length === 0 ? 'none' : pins.some((p) => p.digit !== null && p.named === 'proposed') ? 'proposed' : 'named',
    unsupported: scan !== null && pins.some((p) => witnessPin(p, scan).kind === 'unsupported'),
  };
}

/** What the panel needs from the running subject, asked at click time. */
export interface HandsPanelDeps {
  /**
   * ONE RECEIPT (XD note 2026-09-27 §3.1, PR 4): the status mark for a card's subject, read from the
   * same Body progress the section headers draw — so a card and its header can never disagree.
   * null = the object is not counted (absent).
   */
  readonly mark?: (subject: string) => HTMLElement | null;

  /** Hands on the CURRENT subject — re-asked on every render, never captured. */
  candidates(): readonly HandCandidate[];
  /** Measure one hand at one resolution, or throw a refusal to display. */
  measure(candidate: HandCandidate, resolution: number): HandEvidence;
  /**
   * The cross-resolution consensus sweep for one hand (tip-consensus.ts) —
   * memoized per subject by the wirer, so calling it on every render is free
   * after the first. The candidates it returns are the ghosts on the model and
   * the source of every pre-filled name.
   */
  scan(candidate: HandCandidate): Promise<TipConsensus>;
  /**
   * Commit the pins and re-run the Document. The panel hands over the confirmer
   * identity it minted from a trusted event; it does not construct Documents
   * itself (ADR-007 §4 — the loader owns the lineage).
   */
  declare(
    candidate: HandCandidate,
    args: {
      resolution: number;
      /**
       * Named tips in BIND-WORLD — what the person pointed at (ADR-022 §3.2) —
       * each optionally carrying its first knuckle (ADR-024 §2.2).
       */
      pins: readonly {
        digit: string;
        tip: { x: number; y: number; z: number };
        knuckle?: { x: number; y: number; z: number };
      }[];
      confirmedBy: string;
      /** Recenter the wrist bind into the wrist volume with this graft. */
      recenterWrist: boolean;
      /** WHERE THE WRIST IS, bind-world (ADR-025) — the person's click, or absent. */
      wrist?: { x: number; y: number; z: number };
      /** The thumb's opposition angle, degrees (field 2026-08-15) — per hand. */
      thumbOpposition: number;
    },
  ): void;
  /**
   * REPLACE FINGERS (2026-09-29): open a hand that already HAS digits (a reopened graft, a Tripo
   * preset) for pinning FRESH, as a fingerless hand is — nothing seeded from the old fingers, whose
   * weights may be the very thing that is wrong (Daria's wing rides them). Resolves the state to
   * seed, or null when there is nothing to replace (the wirer says why on the status line).
   * Absent = the door is not offered.
   */
  replaceHand?(handBone: string): Promise<HandUiState | null>;
  /** Who is confirming, once a trusted gesture has established it. */
  confirmerId(): string;
  /**
   * Arm the pin gesture over the model, or put it away (null). While armed, a
   * trusted click on the subject arrives here as a BIND-WORLD point; the pose
   * gizmo steps aside for as long as it is on (one canvas, one meaning per
   * click — ADR-006 §13.2).
   */
  armPinning(
    handlers: {
      /** A click on a marker DISC — the target a person actually aims at. */
      onMarker(handBone: string, markerId: string): void;
      /** The disc under the pointer, so the panel and the model highlight together. */
      onHover(handBone: string | null, markerId: string | null): void;
      onPin(hit: PinHit): void;
      /** The click missed the model — said out loud, because a hand is small. */
      onMiss(): void;
      onRefusal(message: string): void;
    } | null,
  ): void;
  /**
   * Hold the subject still while pinning, and let it go afterwards (Allen,
   * 2026-08-12): a fingertip is a two-millimetre target, and on a walking model
   * it has moved by the time the click lands.
   *
   * IT PAUSES PLAYBACK; IT DOES NOT CHANGE MODE. The first version called for
   * pose mode, which is the same thing to a person and a very different thing
   * to the shell: a mode change re-mounts the panels for that mode, which
   * disposed THIS panel — and with it the armed gesture — between arming and
   * the click. The e2e caught it as a click that did nothing at all. A person
   * asked to pin a digit, not to change what the workspace is for.
   */
  holdStill(hold: boolean): void;
  /** Draw this hand's pins and ghosts on the model. */
  showPins(
    handBoneName: string,
    view: {
      pins: readonly PinMarker[];
      ghosts: readonly GhostMarker[];
      hovered: string | null;
      region: HandEvidence['region'];
    },
  ): void;
  /** Fly the camera to this hand — it is a few centimetres of a whole body. */
  focusHand(evidence: HandEvidence): void;
  /**
   * Take every marker off the model. The dots are parented to the hand BONES,
   * not to this panel's DOM, so closing the panel or collapsing its zone would
   * otherwise leave them drawn over a character nobody is pinning — with their
   * GPU resources retained until some later redraw happened to dispose them
   * (Codex P2, #94: the layer had a `clear()` and no caller).
   */
  clearMarkers(): void;
  /** Where a marker disc is on screen right now — the flyout's anchor. */
  markerScreenXY(handBone: string, markerId: string): { readonly x: number; readonly y: number } | null;
  /** Open a draft of the current hand without restoring an old Document. */
  editHand(handBone: string): Promise<boolean>;
  canEdit(handBone: string): boolean;
  savedState(handBone: string): HandUiState | null;
  /**
   * Each hand's live pinning reading after a render (null = not being pinned:
   * a fingered hand). The Body header reads it; optional, display only.
   */
  onReading?(handBone: string, reading: HandLiveReading | null): void;
  /**
   * THE BODY VOICE (XD 2026-09-29 §3B; Allen: "i tend to lean status"): the analyzer's opinion and
   * a ghost's numbers go to the status line on hover and focus, never onto the card. Optional.
   */
  readonly say?: (line: string | null, reveal?: boolean) => void;
}

/**
 * The finger-pinning how-to — HELP, not panel text (XD 2026-09-29 §3B): the `?` beside Pin fingers,
 * the Elbows card's idiom (describeButton in `.elbow-head`). It was a <details> under the hands
 * (~95 words), opened by the few who needed it and scrolled past by everyone else.
 */
const PIN_HOWTO = [
  'Choose Pin fingers to pause the animation and bring that hand into view.',
  'Click a fingertip or a detected dot, then choose its finger name. The named dots match the finger colors.',
  'Detected dots are suggestions. Skip any that are not fingers; use the remove button to hide one and Put back to restore it.',
  'If the wrist joint sits inside the palm, click the wrist crease and choose Wrist. Otherwise leave it as it is.',
  'Naming all expected fingers creates the finger bones automatically. If you are placing knuckles, finish those and choose Graft now.',
  'Graft this hand lets you finish with fewer fingers. Edit pins reopens your saved pins so you can adjust them.',
]
  .map((step, i) => `${i + 1}. ${step}`)
  .join(' ');

export function createHandsPanel(deps: HandsPanelDeps): PanelMount {
  return (container, store) => {
    container.classList.add('hands-panel');
    const ui = new Map<string, HandUiState>();
    // Sweep tracking, keyed by hand and GUARDED by promise identity: the wirer
    // memoizes deps.scan per subject, so a replaced subject hands back a
    // different promise and the stale sweep is unreachable by construction.
    // Handlers attach exactly once per promise — an entry existing for the
    // current promise means the outcome is pending, landed, or failed, and no
    // path re-attaches (a rejected promise re-observed every render would
    // otherwise render-loop forever).
    interface ScanEntry {
      readonly key: Promise<TipConsensus>;
      value?: TipConsensus;
      error?: string;
    }
    const scans = new Map<string, ScanEntry>();
    let disposed = false;

    const stateFor = (handBone: string): HandUiState => {
      const found = ui.get(handBone);
      if (found) return found;
      const saved = deps.savedState(handBone);
      const fresh = saved ?? initialHandUi();
      if (saved !== null) restored.add(handBone);
      ui.set(handBone, fresh);
      return fresh;
    };

    // Display-only view state (never a decision): which pin rows are open for
    // re-naming, whether the candidate numbers are disclosed, and the last
    // thing the gesture said (a miss, a refusal) so it can be shown once.
    interface ViewState {
      readonly expanded: Set<string>;
      showCandidates: boolean;
      note: string | null;
      hovered: string | null;
    }
    const view = new Map<string, ViewState>();
    const viewFor = (handBone: string): ViewState => {
      const found = view.get(handBone);
      if (found) return found;
      const fresh: ViewState = { expanded: new Set<string>(), showCandidates: false, note: null, hovered: null };
      view.set(handBone, fresh);
      return fresh;
    };

    // An edited hand waits for an explicit apply instead of auto-grafting
    // as soon as the first restored pin changes.
    const restored = new Set<string>();

    /** The hand whose pin gesture is armed, if any — at most one, always. */
    let armedHand: string | null = null;
    /**
     * The marker the digit bar is attached to. `provisional` marks a pin this
     * opening created (a bare-mesh click) rather than one that already existed
     * — the difference between dismissing a question and deleting an answer.
     */
    let flyoutAt: { hand: string; marker: string; provisional: boolean; wristOnly?: boolean } | null = null;

    /**
     * THE DIGIT BAR (XD §1.1 — evidence lives on the character). It is created
     * once and re-aimed, rather than built per click: a bar that is destroyed
     * and rebuilt under the pointer is the same bug that made candidate chips
     * unclickable, one layer up.
     */
    const flyout = createDigitFlyout(document.body, {
      screenXY: () => (flyoutAt === null ? null : deps.markerScreenXY(flyoutAt.hand, flyoutAt.marker)),
      onPick: (digit) => {
        if (flyoutAt === null) return;
        nameFromFlyout(flyoutAt.hand, flyoutAt.marker, digit);
      },
      onPickKnuckle: (digit) => {
        if (flyoutAt === null) return;
        knuckleFromFlyout(flyoutAt.hand, flyoutAt.marker, digit);
      },
      onPickWrist: () => {
        if (flyoutAt === null) return;
        wristFromFlyout(flyoutAt.hand, flyoutAt.marker);
      },
      onCount: (next) => {
        if (flyoutAt === null) return;
        const hand = flyoutAt.hand;
        // THE COUNT CAN INVALIDATE A NAME (Codex P1, #94). A hand of three is
        // Thumb · Index · PINKY, so a pin called `Middle` is not surplus — it
        // is a name this hand does not have. Reconciling first means the graft
        // below can never run on one.
        const counted = setExpected(stateFor(hand), next);
        ui.set(hand, withPins(counted, reconcileNames(counted.pins, offeredDigits(counted.expected))));
        refreshFlyout();
        render();
        // THE COUNT IS THE OTHER WAY TO FINISH (Codex P2, #94). Naming the last
        // digit is one route to `namingComplete`; pressing − until the count
        // meets the names already given is the other, and it is the route a
        // three-fingered hand takes. Checking completion only on the naming
        // side left that person hunting for the fallback button — which is
        // exactly the "find the graft control" step this arc deleted.
        const candidate = deps.candidates().find((c) => c.handBone === hand);
        if (candidate !== undefined) maybeGraft(candidate);
      },
      onReject: () => {
        if (flyoutAt === null) return;
        rejectFromFlyout(flyoutAt.hand, flyoutAt.marker);
      },
      // ESCAPE MUST LEAVE NOTHING BEHIND (Codex P2, #94). A bare-mesh click
      // creates the pin before the name exists — it has to, because the bar
      // anchors to the pin's own dot — so dismissing without naming would
      // otherwise strand an unnamed marker, and repeated abandoned clicks would
      // litter the hand with them. A pin that was already there is untouched:
      // re-opening an answered question and thinking better of it must not
      // delete the answer.
      onDismiss: () => dismissFlyout(),
    });

    function closeFlyout(): void {
      flyoutAt = null;
      flyout.close();
    }

    /** Close without answering, taking any pin this opening invented with it. */
    function dismissFlyout(): void {
      const at = flyoutAt;
      closeFlyout();
      if (at === null || !at.provisional) return;
      const state = stateFor(at.hand);
      const pin = state.pins.pins.find((p) => p.id === at.marker);
      if (pin === undefined || pin.digit !== null) return; // named in the meantime — keep it
      ui.set(at.hand, withPins(state, removePin(state.pins, at.marker)));
      render();
    }

    /** The knuckle marker id, split — `pin-3:knuckle` names pin-3's second row. */
    const knuckleOwner = (markerId: string): string | null =>
      markerId.endsWith(':knuckle') ? markerId.slice(0, -':knuckle'.length) : null;

    /** The second row's offer: digits with a NAMED pin, and which wear one already. */
    function knuckleRowState(handBone: string): {
      knuckleTargets: readonly DigitName[];
      knuckleTaken: readonly DigitName[];
    } {
      const pins = stateFor(handBone).pins.pins;
      return {
        knuckleTargets: pins.map((p) => p.digit).filter((d): d is DigitName => d !== null),
        knuckleTaken: pins.filter((p) => p.knuckle !== null && p.digit !== null).map((p) => p.digit as DigitName),
      };
    }

    /** Re-hand the bar its current state (names taken, the ± value). */
    function refreshFlyout(): void {
      if (flyoutAt === null || !flyout.isOpen) return;
      const state = stateFor(flyoutAt.hand);
      const owner = knuckleOwner(flyoutAt.marker);
      if (owner !== null) {
        const ownerPin = state.pins.pins.find((p) => p.id === owner);
        flyout.update({
          expected: state.expected,
          taken: [],
          current: null,
          ...knuckleRowState(flyoutAt.hand),
          knuckleCurrent: ownerPin?.digit ?? null,
        });
        return;
      }
      const pin = state.pins.pins.find((p) => p.id === flyoutAt!.marker);
      flyout.update({
        expected: state.expected,
        taken: state.pins.pins.map((p) => p.digit).filter((d): d is DigitName => d !== null),
        current: pin?.digit ?? null,
        ...(flyoutAt.provisional
          ? flyoutAt.wristOnly === true
            ? { wristOffered: state.wrist === null, wristOnly: true }
            : { ...knuckleRowState(flyoutAt.hand), wristOffered: state.wrist === null }
          : {}),
      });
    }

    /**
     * A name was chosen on the bar. The pin is created HERE and not on the
     * click that opened it: a marker a person opened and then dismissed should
     * leave nothing behind, and an unnamed pin they never asked for is exactly
     * the kind of debris that makes a surface feel like it is arguing.
     */
    function nameFromFlyout(handBone: string, markerId: string, digit: DigitName): void {
      const candidate = deps.candidates().find((c) => c.handBone === handBone);
      if (candidate === undefined) return;
      const state = stateFor(handBone);
      const existing = state.pins.pins.find((p) => p.id === markerId);
      if (existing !== undefined) {
        ui.set(handBone, withPins(state, namePin(state.pins, markerId, digit)));
      } else {
        const scan = scans.get(handBone)?.value;
        const cluster = scan?.clusters.find((c) => c.id === markerId);
        if (cluster === undefined) return;
        ui.set(handBone, withPins(state, adoptCluster(state.pins, cluster, digit, 'human')));
      }
      closeFlyout();
      render();
      maybeGraft(candidate);
    }

    /**
     * A KNUCKLE was chosen on the bar (ADR-024 §2.2). Two contexts share it:
     * a bare-mesh click (the provisional pin's own place becomes the knuckle
     * of the digit picked, and the provisional pin dissolves — the click was
     * an answer about the row, not a new tip), and an existing knuckle marker
     * being re-aimed at a different digit (the place moves pins).
     */
    function knuckleFromFlyout(handBone: string, markerId: string, digit: DigitName): void {
      const state = stateFor(handBone);
      const target = state.pins.pins.find((p) => p.digit === digit);
      if (target === undefined) return; // the row only offers named digits, but state may have moved
      const owner = knuckleOwner(markerId);
      if (owner !== null) {
        const from = state.pins.pins.find((p) => p.id === owner);
        if (from === undefined || from.knuckle === null) return;
        const moved = setKnuckle(setKnuckle(state.pins, owner, null), target.id, from.knuckle);
        ui.set(handBone, withPins(state, moved));
      } else {
        const at = state.pins.pins.find((p) => p.id === markerId);
        if (at === undefined) return;
        // The provisional pin's place IS the knuckle; the pin itself dissolves.
        const pins = setKnuckle(removePin(state.pins, at.id), target.id, at.tip);
        ui.set(handBone, withPins(state, pins));
      }
      closeFlyout();
      render();
    }

    /**
     * THE WRIST was chosen on the bar (ADR-025): the provisional pin's own place
     * becomes this hand's wrist station and the pin dissolves — the click was an
     * answer about the wrist, not a new tip. A second wrist click MOVES it: one
     * wrist per hand, the latest click wins, nothing accumulates.
     */
    function wristFromFlyout(handBone: string, markerId: string): void {
      const state = stateFor(handBone);
      const at = state.pins.pins.find((p) => p.id === markerId);
      if (at === undefined) return;
      ui.set(handBone, setWrist(withPins(state, removePin(state.pins, at.id)), at.tip));
      closeFlyout();
      render();
    }

    /** The bar's × — throw the candidate out, take the pin away, clear a knuckle, or clear the wrist. */
    function rejectFromFlyout(handBone: string, markerId: string): void {
      const state = stateFor(handBone);
      if (markerId === WRIST_MARKER_ID) {
        ui.set(handBone, setWrist(state, null));
        closeFlyout();
        render();
        return;
      }
      const owner = knuckleOwner(markerId);
      const pins =
        owner !== null
          ? setKnuckle(state.pins, owner, null)
          : state.pins.pins.some((p) => p.id === markerId)
            ? removePin(state.pins, markerId)
            : rejectCluster(state.pins, markerId);
      ui.set(handBone, withPins(state, pins));
      closeFlyout();
      render();
    }

    /**
     * ★ THE GRAFT RUNS ITSELF once every digit this hand is believed to have
     *   has been named — XD §5.6 **gate 4**: *"Can the engine settle it
     *   honestly, on its own? → TELL. It's already done. Say what was done and
     *   how to undo it."*
     *
     *   This is not the engine confirming its own read, which §8 forbids and
     *   which grafted a phantom finger onto the kobold. Every name in the count
     *   came from a trusted human click on a place they pointed at; there is
     *   nothing left for a confirm button to add except a click. What the law
     *   demands instead is that the action ANNOUNCE ITSELF and leave a door —
     *   `editHand` is that door, and the card renders it after the fact.
     *
     *   Allen, ruling it: *"as soon as i name the fourth, it commands the graft
     *   and the octabones appear — that would be effortless."*
     */
    function maybeGraft(candidate: HandCandidate): void {
      const state = stateFor(candidate.handBone);
      if (!namingComplete(state) || !canGraft(state)) return;
      // THE KNUCKLE WORKFLOW HOLDS THE DOOR (Allen, field 2026-08-14): firing
      // on the fifth name made the fifth digit's knuckle unpinnable. One or
      // more knuckles on the hand = the two-row workflow is in progress, so
      // completion renders the Graft-now chip instead (see renderHand); a hand
      // with no knuckles keeps the instant graft exactly as ruled. A hand
      // brought back through the revert door holds too — the person reverted
      // to keep working on it, and re-firing on their first re-name would
      // spring the same trap the restore exists to open.
      if (graftHolds(state) || restored.has(candidate.handBone)) {
        render();
        return;
      }
      runGraft(candidate, state);
    }

    /**
     * Compose and hand over. ONE path, whether the last name triggered it or a
     * person pressed the button: two graft paths would be two places for the
     * pins, the recenter and the confirmer to be assembled differently.
     */
    function runGraft(candidate: HandCandidate, state: HandUiState): void {
      // The gesture goes away with the graft: the subject is about to be
      // replaced, and a live pin tool aimed at a model that no longer exists is
      // a click with nowhere to land.
      if (armedHand === candidate.handBone) disarm();
      closeFlyout();
      restored.delete(candidate.handBone);
      deps.declare(candidate, {
        resolution: state.resolution,
        pins: namedPins(state.pins).map((p) => ({
          digit: String(p.digit),
          tip: p.tip,
          ...(p.knuckle === null ? {} : { knuckle: p.knuckle }),
        })),
        recenterWrist: state.recenterWrist,
        ...(state.wrist === null ? {} : { wrist: state.wrist }),
        thumbOpposition: state.thumbOpposition,
        confirmedBy: deps.confirmerId(),
      });
    }


    /**
     * HOVER REPAINTS THE MODEL AND TOUCHES THE DOM ONLY BY CLASS.
     *
     * The first version called `render()` from every hover handler, which
     * rebuilt the card — and therefore destroyed the very element the pointer
     * was over. A click after a hover then landed on a detached node and did
     * nothing at all: Playwright reported "element was detached from the DOM,
     * retrying" 238 times, and it is almost certainly what Allen hit when he
     * could see a ring finger and *"nothing i did could get me a target"* on it.
     * A surface that rebuilds itself under the cursor cannot be clicked.
     *
     * So highlight state is applied in place. The marker layer redraws (it is
     * meant to), and the panel only adds or removes a class.
     */
    function applyHover(handBone: string): void {
      const v = viewFor(handBone);
      const card = container.querySelector(`.hand-card[data-hand="${handBone}"]`);
      if (card === null) return;
      for (const row of card.querySelectorAll('[data-pin]')) {
        row.classList.toggle('pin-row-hovered', (row as HTMLElement).dataset['pin'] === v.hovered);
      }
      for (const chip of card.querySelectorAll('.candidate-chip')) {
        chip.classList.toggle('candidate-chip-hovered', (chip as HTMLElement).dataset['cluster'] === v.hovered);
      }
    }

    /** Set the hovered marker and repaint — model always, DOM by class only. */
    function setHovered(candidate: HandCandidate, id: string | null): void {
      const v = viewFor(candidate.handBone);
      if (v.hovered === id) return;
      v.hovered = id;
      applyHover(candidate.handBone);
      const state = stateFor(candidate.handBone);
      const scan = scans.get(candidate.handBone)?.value;
      if (scan === undefined) return;
      try {
        drawMarkers(candidate, deps.measure(candidate, state.resolution), scan, state);
      } catch {
        // The card renders its own refusal; a hover must not raise one.
      }
    }

    /** The sweep entry for this hand's CURRENT promise: pending, landed, or failed. */
    const scanFor = (candidate: HandCandidate): ScanEntry => {
      const promise = deps.scan(candidate);
      const cached = scans.get(candidate.handBone);
      if (cached && cached.key === promise) return cached;
      if (cached) {
        // A DIFFERENT promise for the same bone name means the SUBJECT was
        // replaced — and a pin is a judgement of THIS model's anatomy, so it
        // must not survive onto a different model that happens to reuse the
        // bone name (§8). The pins and the view state retire with the sweep
        // they were made against, and an armed gesture is put away with them.
        ui.delete(candidate.handBone);
        view.delete(candidate.handBone);
        restored.delete(candidate.handBone);
        if (armedHand === candidate.handBone) disarm();
      }
      const entry: ScanEntry = { key: promise };
      scans.set(candidate.handBone, entry);
      promise.then(
        (value) => {
          if (disposed || scans.get(candidate.handBone) !== entry) return;
          entry.value = value;
          render();
        },
        (err: unknown) => {
          if (disposed || scans.get(candidate.handBone) !== entry) return;
          // A sweep that DIES is a finding to display, not a forever-spinner.
          entry.error = err instanceof Error ? err.message : String(err);
          render();
        },
      );
      return entry;
    };

    function disarm(): void {
      armedHand = null;
      deps.armPinning(null);
      // The clip resumes: the hold was for the gesture, and the gesture is over.
      deps.holdStill(false);
    }

    /**
     * Arm the gesture for one hand. The handler closes over the CANDIDATE only —
     * every other input (state, evidence, scan) is re-read when the click
     * arrives, because a click can land many renders after the arming and a
     * captured `state` would write pins into a stale set.
     */
    function arm(candidate: HandCandidate): void {
      armedHand = candidate.handBone;
      // Hold still and come close. Both are part of "I want to pin a digit":
      // the clip stops (a fingertip on a walking model has moved by the time the
      // click lands) and the camera frames the hand (it is centimetres of a
      // whole body). Neither is a mode the person has to know about.
      deps.holdStill(true);
      deps.armPinning({
        onMarker: (handBone, markerId) => {
          if (handBone !== candidate.handBone) return; // another hand's disc — not this card's business
          clickMarker(candidate, markerId);
        },
        onHover: (handBone, markerId) => setHovered(candidate, handBone === candidate.handBone ? markerId : null),
        onPin: (hit) => landPin(candidate, hit),
        onMiss: () => {
          const v = viewFor(candidate.handBone);
          v.note = 'That click missed the model. A hand is small on screen — try again on the fingertip, or zoom in first.';
          render();
        },
        onRefusal: (message) => {
          const v = viewFor(candidate.handBone);
          v.note = message;
          render();
        },
      });
    }

    /**
     * A click landed ON A DISC. A candidate disc becomes a pin at its measured
     * tip; a pin that is already there opens for renaming. Either way the click
     * is about the thing under the cursor, which is the whole reason the discs
     * are the pick surface.
     */
    function clickMarker(candidate: HandCandidate, markerId: string): void {
      const state = stateFor(candidate.handBone);
      const v = viewFor(candidate.handBone);
      v.note = null;
      // THE WRIST mark opens the wrist question (ADR-025): keep it or take it
      // away. Moving it is a new click on the model.
      if (markerId === WRIST_MARKER_ID) {
        if (state.wrist === null) return;
        dismissFlyout();
        flyoutAt = { hand: candidate.handBone, marker: markerId, provisional: false };
        flyout.open({ expected: state.expected, taken: [], current: null, wristCurrent: true });
        render();
        return;
      }
      // A KNUCKLE disc opens a knuckle question (ADR-024 §2.2): whose is it,
      // or take it away. The tip row would be noise on it.
      const owner = knuckleOwner(markerId);
      if (owner !== null) {
        const ownerPin = state.pins.pins.find((p) => p.id === owner);
        if (ownerPin === undefined || ownerPin.knuckle === null) return;
        dismissFlyout();
        flyoutAt = { hand: candidate.handBone, marker: markerId, provisional: false };
        flyout.open({
          expected: state.expected,
          taken: [],
          current: null,
          ...knuckleRowState(candidate.handBone),
          knuckleCurrent: ownerPin.digit,
        });
        render();
        return;
      }
      const pin = state.pins.pins.find((p) => p.id === markerId);
      const scan = scans.get(candidate.handBone)?.value;
      if (pin === undefined && scan?.clusters.some((c) => c.id === markerId) !== true) return;
      // RE-AIMING IS A DISMISSAL (Codex P2, #94). Clicking a different disc
      // while a provisional pin is waiting for its name used to overwrite the
      // only reference to it — Escape could then no longer find it, and every
      // redirected click stranded another unnamed marker on the hand.
      dismissFlyout();
      // The bar flies out AT THE DISC. Nothing is written yet — opening a
      // question is not answering it.
      flyoutAt = { hand: candidate.handBone, marker: markerId, provisional: false };
      flyout.open({
        expected: state.expected,
        taken: state.pins.pins.map((p) => p.digit).filter((d): d is DigitName => d !== null),
        current: pin?.digit ?? null,
      });
      render();
    }

    /**
     * A click landed on the MESH. Two questions, in order: is it on THIS hand,
     * and is there a name to propose for it?
     */
    function landPin(candidate: HandCandidate, hit: PinHit): void {
      const state = stateFor(candidate.handBone);
      const v = viewFor(candidate.handBone);
      let evidence: HandEvidence;
      try {
        evidence = deps.measure(candidate, state.resolution);
      } catch (err) {
        v.note = err instanceof Error ? err.message : String(err);
        render();
        return;
      }
      // ON THIS HAND, OR NOT A PIN. With the right-hand card open a person can
      // and will click the left hand, or the hip. Placing a pin there would
      // graft a finger somewhere nobody meant — and the graft-time check would
      // refuse it only after they had answered every other question.
      //
      // MEMBERSHIP, NOT DISTANCE. The first version measured to the nearest
      // region vertex and refused anything past two voxels; the e2e then aimed
      // a click dead at the goblin's fingertip and got refused at 6 mm, because
      // on a low-poly hand the nearest VERTEX to a surface point is millimetres
      // away and the threshold could not tell that from a click on the hip.
      // The region is a set of vertex indices, so asking whether the clicked
      // triangle is in it has nothing to tune and behaves the same at any mesh
      // density.
      // …EXCEPT THE WRIST (ADR-025, field 2026-08-15 — Kara: "very resistant to
      // placing the wrist"). The wrist crease is forearm-weighted flesh BY
      // DEFINITION — on the overshoot class it is precisely the flesh Tripo
      // gave the forearm — so a membership gate refuses the click at the one
      // spot the answer lives. An off-hand click therefore is not a refusal
      // while the wrist is unanswered: it opens the bar with ONLY the wrist
      // offer (it cannot be a tip; it can be the wrist). The station's own
      // limits at graft time (proximal of the forearm midpoint / distal beyond
      // the hand refuse by name) are the guard against a click on the hip.
      //
      // AND ONCE THE WRIST IS ANSWERED, THE FLESH IT WILL RE-OWN IS PINNABLE
      // (Codex P1, #97): on the overshoot class the palm root and the thumb
      // lobe are forearm-dominated — exactly what the station reclaims at graft
      // time — so refusing off-region tip/knuckle clicks after the wrist answer
      // would make the guided workflow unable to describe the very region it
      // is repairing. The envelope is the station's: within the hand's own span
      // of the wrist mark. Beyond that (the other hand, the hip) still refuses
      // here, and the graft-time witness (a pin off the volume refuses by
      // distance) stays the last word.
      const offRegion = !faceInRegion(hit.face, evidence.region);
      const scanForReach = scans.get(candidate.handBone)?.value;
      const nearWrist =
        state.wrist !== null &&
        scanForReach !== undefined &&
        Math.hypot(hit.bindWorld.x - state.wrist.x, hit.bindWorld.y - state.wrist.y, hit.bindWorld.z - state.wrist.z) <=
          1.25 * scanForReach.span;
      const offHand = offRegion && !nearWrist;
      if (offHand && state.wrist !== null) {
        v.note =
          `That click did not land on ${candidate.handBone} — the geometry there is weighted to another bone, and it is ` +
          `beyond this hand's span from the wrist mark. Pin the hand this card is about, or open the other hand's card. ` +
          `(To move the wrist, click its mark first and remove it.)`;
        render();
        return;
      }

      const scan = scans.get(candidate.handBone)?.value;
      // The candidate this click landed on, if any — carried for the witness,
      // never for a name (see clickMarker: the proposal orders, it never says).
      let clusterId: string | null = null;
      if (scan !== undefined) {
        const radius = CLUSTER_RADIUS_FRACTION * scan.span;
        let nearest = radius;
        for (const cluster of scan.clusters) {
          const d = Math.hypot(
            hit.bindWorld.x - cluster.tip.x,
            hit.bindWorld.y - cluster.tip.y,
            hit.bindWorld.z - cluster.tip.z,
          );
          if (d < nearest) {
            nearest = d;
            clusterId = cluster.id;
          }
        }
      }
      const pins = addPin(state.pins, { tip: hit.bindWorld, digit: null, named: 'human', clusterId });
      ui.set(candidate.handBone, withPins(state, pins));
      v.note = null;
      // A pin placed on bare mesh — a fused digit, a claw the volume never
      // separated — asks its question in the same place every other one does:
      // the bar flies out on the dot that just appeared.
      const fresh = pins.pins[pins.pins.length - 1]!;
      // Whatever the last opening invented goes with it — same rule as above.
      dismissFlyout();
      // ★ DRAW IT FIRST (Codex P2, #94). The bar anchors to the marker's own
      //   dot, and `open()` asks for that dot's screen position immediately —
      //   so opening before the render that CREATES the dot got `null`, and the
      //   bar closed itself on its first frame. The person was left with an
      //   unnamed pin and no way to name it. This path is not an edge case: it
      //   is the fused digit, the claw no resolution separates, the whole
      //   reason a pin may exist without a candidate under it.
      render();
      flyoutAt = { hand: candidate.handBone, marker: fresh.id, provisional: true, wristOnly: offHand };
      // A bare-mesh click can be a TIP question or a KNUCKLE question (ADR-024
      // §2.2) — the second row offers the digits that already have a named
      // tip, and picking one dissolves this provisional pin into that digit's
      // knuckle. The row is only as long as the answers so far, which is
      // Allen's stated order: name the tips, then click the knuckles.
      // …and, FIRST, a WRIST question (ADR-025 D3, Allen's order: wrist →
      // tips → knuckles): on a hand with no wrist answer the bar leads with it.
      flyout.open({
        expected: state.expected,
        taken: pins.pins.map((p) => p.digit).filter((d): d is DigitName => d !== null),
        current: null,
        ...(offHand ? {} : knuckleRowState(candidate.handBone)),
        wristOffered: state.wrist === null,
        wristOnly: offHand,
      });
    }

    function render(): void {
      if (disposed) return;
      container.replaceChildren();
      const candidates = deps.candidates();

      // THE ARMED HAND MUST STILL EXIST (Codex P2, #94). A new subject can
      // arrive while pinning is armed, and if its rig has no candidate by that
      // name — a different creature, or no mapped hands at all — nothing was
      // clearing the arming: the pin tool went on raycasting a subject that had
      // left the stage, the crosshair stayed up, and `pinHold` left the NEW
      // model paused. `scanFor` only retires a hand that is still offered under
      // the same name, so the case it cannot see is exactly this one.
      if (armedHand !== null && !candidates.some((c) => c.handBone === armedHand)) disarm();

      if (candidates.length === 0) {
        // Absence is a finding, said as one — never a blank panel implying
        // "nothing to do here" (§8).
        // KEEP the finding, short (§3B); the consequence is said on the status line.
        const empty = document.createElement('p');
        empty.className = 'hands-empty';
        empty.textContent = 'No mapped hand.';
        empty.addEventListener('pointerenter', () => deps.say?.('hands: no mapped hand on this rig. Nothing can be grafted until the mapping names one.'));
        container.append(empty);
        return;
      }

      for (const candidate of candidates) {
        container.append(renderHand(candidate));
        deps.onReading?.(
          candidate.handBone,
          dispositionOf(candidate).kind === 'already-fingered'
            ? null
            : handLiveReading(stateFor(candidate.handBone), scans.get(candidate.handBone)?.value ?? null),
        );
      }
    }

    /**
     * The voxel-resolution control — the ONE cure the analyzer names when it
     * refuses ("at least two persistent distal branches, or higher
     * resolution"), so it must render on the refusal paths too. It used to
     * live only inside "What the volume saw" (now "Fine-tune", 2026-09-27), which is built after the
     * measurement succeeds: a hand that came back `hand-none` showed the
     * cure's name and then hid the knob, and there was no way to re-read the
     * hand at another depth without dropping the character (Allen, 2026-09-07,
     * on Koda's fur-covered hands). A refusal that names a cure must leave the
     * cure reachable.
     */
    function resolutionRow(candidate: HandCandidate, state: HandUiState): HTMLElement {
      const row = document.createElement('div');
      row.className = 'hand-resolution';
      const label = document.createElement('label');
      label.textContent = 'Voxel resolution';
      const input = document.createElement('input');
      input.type = 'range';
      // The schema's own band (ADR-010 §11), so the control cannot express a
      // value the load door would refuse.
      input.min = '8';
      input.max = '128';
      input.step = '2';
      input.value = String(state.resolution);
      input.setAttribute('aria-label', `${candidate.handBone} voxel resolution`);
      const readout = document.createElement('output');
      readout.textContent = String(state.resolution);
      input.addEventListener('input', () => {
        ui.set(candidate.handBone, setResolution(state, Number(input.value)));
        render();
      });
      label.append(input, readout);
      row.append(
        ...withTip(label, 'The depth the volume is read at. Pins do not depend on it — move it only when a digit will not separate at all.'),
      );
      return row;
    }

    function renderHand(candidate: HandCandidate): HTMLElement {
      const state = stateFor(candidate.handBone);
      const section = document.createElement('section');
      section.className = 'hand-card';
      section.dataset['hand'] = candidate.handBone;

      const title = document.createElement('h3');
      title.textContent = candidate.side === 'left' ? 'Left Hand' : 'Right Hand';
      const handMark = deps.mark?.(candidate.side === 'left' ? 'Left hand' : 'Right hand') ?? null;
      if (handMark !== null) title.append(handMark);
      section.append(title);

      // A rig that already has fingers is not a graft candidate — the kernel
      // refuses the ontology-id collision, so say so here instead of letting a
      // person spend a pin on it.
      const disposition = dispositionOf(candidate);
      if (disposition.kind === 'already-fingered') {
        if (armedHand === candidate.handBone) disarm();
        // The title's mark is the receipt (◉ yours / · fine as is); the line said it in words only
        // where no mark is drawn.
        if (handMark === null) {
          const already = document.createElement('p');
          already.className = 'hand-already';
          already.textContent = deps.canEdit(candidate.handBone) ? 'Fingers pinned' : 'Fingers ready';
          section.append(already);
        }
        if (deps.canEdit(candidate.handBone)) {
          const edit = document.createElement('button');
          edit.type = 'button';
          edit.className = 'hand-revert';
          edit.textContent = 'Edit pins';
          edit.addEventListener('click', event => {
            if (!event.isTrusted) return;
            const saved = deps.savedState(candidate.handBone);
            void deps.editHand(candidate.handBone).then(opened => {
              if (disposed || !opened || saved === null) return;
              const back = deps.candidates().find(c => c.handBone === candidate.handBone);
              if (!back) return;
              scanFor(back);
              ui.set(candidate.handBone, saved);
              restored.add(candidate.handBone);
              if (armedHand !== null) disarm();
              arm(back);
              ui.set(candidate.handBone, setArming(saved, true));
              try {
                deps.focusHand(deps.measure(back, saved.resolution));
              } catch (err) {
                viewFor(candidate.handBone).note = err instanceof Error ? err.message : String(err);
              }
              render();
            });
          });
          section.append(edit);
        } else if (deps.replaceHand !== undefined) {
          // Fingers the Document did not make (a reopened export's graft, a Tripo preset's own):
          // no saved pins to edit, so the door starts over from where these fingers end.
          const replace = document.createElement('button');
          replace.type = 'button';
          replace.className = 'hand-revert';
          replace.textContent = 'Replace fingers';
          replace.addEventListener('click', (event) => {
            if (!event.isTrusted) return;
            void deps.replaceHand!(candidate.handBone).then((seed) => {
              if (disposed || seed === null) return;
              const back = deps.candidates().find((c) => c.handBone === candidate.handBone);
              if (!back) return;
              scanFor(back);
              // NOT `restored`: there are no saved pins to adjust — this is the hand's first graft,
              // so the card offers Graft, not "Apply hand changes".
              ui.set(candidate.handBone, seed);
              if (armedHand !== null) disarm();
              arm(back);
              ui.set(candidate.handBone, setArming(seed, true));
              try {
                deps.focusHand(deps.measure(back, seed.resolution));
              } catch (err) {
                viewFor(candidate.handBone).note = err instanceof Error ? err.message : String(err);
              }
              render();
            });
          });
          section.append(...withTip(replace, 'Drop these fingers and pin the hand fresh — for a reopened graft, or a rig whose own fingers are wrong.'));
        }
        return section;
      }

      let evidence: HandEvidence;
      try {
        evidence = deps.measure(candidate, state.resolution);
      } catch (err) {
        const refusal = document.createElement('p');
        refusal.className = 'hand-refusal';
        refusal.textContent = err instanceof Error ? err.message : String(err);
        section.append(refusal);
        // The cure the refusal just named, reachable (see resolutionRow).
        section.append(resolutionRow(candidate, state));
        return section;
      }

      const scan = scanFor(candidate);
      if (scan.error !== undefined) {
        const failed = document.createElement('p');
        failed.className = 'hand-refusal';
        failed.textContent = `The resolution sweep failed: ${scan.error}`;
        section.append(failed);
        section.append(resolutionRow(candidate, state));
        return section;
      }
      if (scan.value === undefined) {
        const busy = document.createElement('p');
        busy.className = 'hand-scanning';
        busy.textContent = 'Finding fingertips…';
        section.append(busy);
        section.append(resolutionRow(candidate, state));
        return section;
      }

      drawMarkers(candidate, evidence, scan.value, state);
      // THE ANALYZER'S OPINION IS SAID (§3B): it was the first line of Fine-tune; it is a reading, so
      // it goes to the status line when the card is hovered or focused.
      const opinion = `${candidate.side} hand: ${readingSummary({
        branchCount: evidence.branches.length,
        profile: evidence.profile,
        confidence: evidence.confidence,
      })} Candidates come from ${scan.value.cleanResolutions.length} swept resolutions.`;
      const sayOpinion = (event: Event): void => {
        // Only a crossing onto the card: a ghost chip inside says its own numbers.
        if (event.type === 'pointerenter' || !(event.target as Element | null)?.closest('.candidate-chip')) deps.say?.(opinion);
      };
      section.addEventListener('pointerenter', sayOpinion);
      section.addEventListener('focusin', sayOpinion);
      // ★ THE WRIST NUDGE (ADR-025 D3, Allen): every digit is named and nobody
      //   said where the wrist is. Not a refusal — a good-wrist character does
      //   not need one — but the one answer that, when it is wrong, lands every
      //   other answer on this hand wrong. A quiet line, and the way to answer.
      if (wristNudge(state)) {
        const nudge = document.createElement('p');
        nudge.className = 'hand-wrist-nudge';
        nudge.textContent =
          'If the wrist needs adjusting, pin its crease.';
        section.append(nudge);
      }
      // ★ THE GRAFT-NOW CHIP: naming is complete but the auto-graft is holding
      //   (see maybeGraft) — knuckles are in play, or the hand came back
      //   through the revert door. The door it holds is the person's to open —
      //   one chip, existing only when it means something.
      if ((restored.has(candidate.handBone) && state.pins.pins.length === 0) ||
          (canGraft(state) && (restored.has(candidate.handBone) || (namingComplete(state) && graftHolds(state))))) {
        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'hand-graft-now';
        go.textContent = restored.has(candidate.handBone)
          ? (state.pins.pins.length === 0 ? 'Remove hand pins' : 'Apply hand changes') : 'Graft now';
        const goTip = restored.has(candidate.handBone) && !graftHolds(state)
          ? 'Adjust your saved pins, then apply the hand changes.'
          : 'Every digit is named. The graft is waiting because you are placing knuckles — ' +
            'add more, or graft with the rows you have.';
        go.addEventListener('click', (event) => {
          if (!event.isTrusted) return;
          runGraft(candidate, stateFor(candidate.handBone));
        });
        section.append(...withTip(go, goTip));
      }
      section.append(
        renderPinning(candidate, evidence, scan.value, state),
        ...(state.pins.pins.length > 0 && !restored.has(candidate.handBone) ? [renderDeclare(candidate, state)] : []),
        renderDiagnostics(candidate, evidence, scan.value, state),
      );
      return section;
    }

    /** Push the current pins and ghosts at the model. */
    function drawMarkers(
      candidate: HandCandidate,
      evidence: HandEvidence,
      scan: TipConsensus,
      state: HandUiState,
    ): void {
      const v = viewFor(candidate.handBone);
      const mapping = resolveAtResolution(scan.clusters, evidence.branches, CLUSTER_RADIUS_FRACTION * scan.span);
      const pins: PinMarker[] = state.pins.pins.map((pin) => {
        const witness = witnessPin(pin, scan);
        const branchId = witness.kind === 'corroborated' ? mapping.get(witness.clusterId) : undefined;
        const chain = branchId === undefined ? undefined : evidence.branches.find((b) => b.id === branchId)?.chainWorld;
        return {
          id: pin.id,
          tipWorld: pin.tip,
          digit: pin.digit,
          state: pin.digit === null ? 'unnamed' : pin.named,
          corroborated: witness.kind === 'corroborated',
          ...(chain === undefined ? {} : { chainWorld: chain }),
          ...(pin.knuckle === null ? {} : { knuckleWorld: pin.knuckle }),
        };
      });
      const ghosts: GhostMarker[] = unpinnedClusters(state.pins, scan).map((c) => ({ id: c.id, tipWorld: c.tip }));
      deps.showPins(candidate.handBone, {
        pins,
        ghosts,
        hovered: v.hovered,
        region: evidence.region,
        ...(state.wrist === null ? {} : { wristWorld: state.wrist }),
      });
    }

    /**
     * The wrist-recenter PROPOSAL (unbind-adjust-rebind #1): the measured
     * offset between the wrist joint and the wrist VOLUME's centre, and a
     * switch — PRE-FILLED ON since 2026-08-02 (Allen's foxDude field report:
     * a 45 mm authored offset sailed through the quiet opt-in). The person
     * unchecks to decline; the measured mm stays visible either way.
     */
    function renderRecenter(candidate: HandCandidate, evidence: HandEvidence, state: HandUiState): HTMLElement {
      const row = document.createElement('label');
      row.className = 'hand-recenter';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = state.recenterWrist;
      box.addEventListener('change', (event) => {
        if (!event.isTrusted) return;
        ui.set(candidate.handBone, toggleRecenterWrist(state));
        render();
      });
      const mm = (evidence.wrist.offsetWorld * 1000).toFixed(0);
      // "Recenter wrist (45 mm)" — the number is the decision; the sentence is its tip (§3B).
      const text = document.createElement('span');
      text.textContent = `Recenter wrist (${mm} mm)`;
      row.append(box, text, attachTip(
        box,
        `Recenter wrist into the volume with the graft — the joint sits ${mm} mm ` +
          `from the wrist's centre (mesh does not move; the rotation pivot does).`,
      ));
      return row;
    }

    /**
     * THE EXPERT DRAWER — everything that is true but not asked for.
     *
     * The voxel resolution used to be the first control on this card, and Allen
     * asked the right question on first use: *"why show voxel resolution to the
     * user? we've already scanned all resolutions"*. Under ADR-022 that is
     * exactly right — the sweep covers the band, pins are world places that
     * survive any resolution, and nothing about the graft depends on where the
     * knob sits. It is not deleted, because when a hand refuses to separate it
     * is the one thing worth moving; it is just no longer in the way of the
     * question a person came here to answer.
     */
    function renderDiagnostics(
      candidate: HandCandidate,
      evidence: HandEvidence,
      scan: TipConsensus,
      state: HandUiState,
    ): HTMLElement {
      const box = document.createElement('details');
      box.className = 'hand-diagnostics';
      const summary = document.createElement('summary');
      summary.textContent = 'Fine-tune';
      box.append(summary);

      // The classification is still reported — hiding it would be its own kind of lie — but as the
      // analyzer's OPINION on the status line (the card's hover, renderHand), not a caption here.

      box.append(resolutionRow(candidate, state));

      box.append(renderRecenter(candidate, evidence, state));

      // THE THUMB'S OPPOSITION (field 2026-08-15): per rig, dialled while the
      // clip plays. Sergio's cupped, wrist-flexed bind is already partly
      // opposed and shears the thumb–index webbing at 90; Kara's flat bind
      // takes 90. Re-graft applies it (frames are authored at graft).
      const thumbRow = document.createElement('div');
      thumbRow.className = 'hand-resolution';
      const thumbLabel = document.createElement('label');
      thumbLabel.textContent = 'Thumb opposition °';
      const thumbInput = document.createElement('input');
      thumbInput.type = 'range';
      thumbInput.min = '-180';
      thumbInput.max = '180';
      thumbInput.step = '5';
      thumbInput.value = String(state.thumbOpposition);
      thumbInput.setAttribute('aria-label', `${candidate.handBone} thumb opposition degrees`);
      const thumbOut = document.createElement('output');
      thumbOut.textContent = String(state.thumbOpposition);
      thumbInput.addEventListener('input', () => {
        ui.set(candidate.handBone, setThumbOpposition(stateFor(candidate.handBone), Number(thumbInput.value)));
        render();
      });
      thumbLabel.append(thumbInput, thumbOut);
      thumbRow.append(
        ...withTip(
          thumbLabel,
          'How far the thumb dorsal is rotated from the fingers toward the radial edge. 90 = anatomical opposition (flat binds). ' +
            'If a clip shears the thumb–index webbing, the bind is already partly opposed — dial down (45, 0). Applies on the next graft.',
        ),
      );
      box.append(thumbRow);
      // The candidate-numbers list is DELETED (§3B): each ghost's numbers are said on the status line
      // when its chip is hovered (renderCandidates) — the one place a person is looking at that ghost.
      void scan;
      return box;
    }

    /**
     * The pin list — the whole question, and the answers so far.
     *
     * GUIDED REVEAL (Allen, 2026-08-12: *"all the user needs to see is pick a
     * digit, then reveal the digit list"*). Before pinning is armed the card
     * shows ONE control. Everything else — names, witnesses, candidate
     * numbers — arrives when it becomes the thing being decided. The panel used
     * to open with a slider, a paragraph of reading, five rows of evidence and a
     * disabled button, which is four decisions presented before the first one
     * was made.
     */
    function renderPinning(
      candidate: HandCandidate,
      evidence: HandEvidence,
      scan: TipConsensus,
      state: HandUiState,
    ): HTMLElement {
      const wrap = document.createElement('div');
      wrap.className = 'hand-pins';
      const v = viewFor(candidate.handBone);
      const armed = armedHand === candidate.handBone;

      const armButton = document.createElement('button');
      armButton.type = 'button';
      armButton.className = armed ? 'pin-arm pin-arm-on' : 'pin-arm';
      armButton.textContent = armed ? 'Done pinning' : state.pins.pins.length > 0 ? 'Pin another finger' : 'Pin fingers';
      const armTip = armed
        ? 'Click the discs on the model to pin each fingertip. Press again when you are finished.'
        : 'Stops the clip, frames the hand, and lets you click each fingertip on the model.';
      armButton.setAttribute('aria-pressed', String(armed));
      armButton.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        if (armed) {
          disarm();
        } else {
          // One hand at a time: arming here takes the gesture from wherever it
          // was, so a click can never mean "a pin on the other hand".
          if (armedHand !== null) disarm();
          arm(candidate);
          deps.focusHand(evidence);
        }
        ui.set(candidate.handBone, setArming(state, !armed));
        render();
      });
      // The how-to is the `?` beside the verb (the `.elbow-head` pattern the Elbows card set).
      const head = document.createElement('div');
      head.className = 'elbow-head';
      head.append(...withTip(armButton, armTip), describeButton('Finger pinning tutorial', () => PIN_HOWTO));
      wrap.append(head);

      if (armed) {
        // THE ONE SENTENCE, and only while it is the live question.
        const how = document.createElement('p');
        how.className = 'pin-empty';
        how.textContent = state.pins.pins.some(pin => pin.digit === null)
          ? 'Choose a finger name for the selected dot.'
          : 'Click a fingertip, then choose its finger name.';
        wrap.append(how);
      }

      if (v.note !== null) {
        // What the gesture said — a miss, a click on the wrong hand, a refusal.
        const note = document.createElement('p');
        note.className = 'pin-note';
        note.textContent = v.note;
        wrap.append(note);
      }

      if (state.pins.pins.length > 0) {
        const list = document.createElement('ul');
        list.className = 'pin-list';
        for (const pin of state.pins.pins) {
          list.append(renderPin(candidate, evidence, scan, state, pin, v));
        }
        wrap.append(list);
      }

      wrap.append(renderCandidates(candidate, scan, state, armed));
      return wrap;
    }

    function renderPin(
      candidate: HandCandidate,
      evidence: HandEvidence,
      scan: TipConsensus,
      state: HandUiState,
      pin: HandPin,
      v: ViewState,
    ): HTMLElement {
      // A ROW IS A RECEIPT, NOT A FORM. Naming happens on the character (the
      // digit bar flies out at the disc), so this says what was answered and
      // gets out of the way — Allen, 2026-08-12: *"the UX is now very busy, for
      // a feature that is super clean."* Clicking a row re-opens the bar on its
      // dot, which is the one thing a receipt should still be able to do.
      const item = document.createElement('li');
      item.className = pin.digit === null ? 'pin-row pin-row-unnamed' : 'pin-row';
      if (v.hovered === pin.id) item.classList.add('pin-row-hovered');
      item.dataset['pin'] = pin.id;
      if (pin.digit !== null) item.dataset['digit'] = pin.digit;
      item.addEventListener('pointerenter', () => setHovered(candidate, pin.id));
      item.addEventListener('pointerleave', () => setHovered(candidate, null));

      const open = document.createElement('button');
      open.type = 'button';
      open.className = 'pin-open';
      open.dataset['pin'] = pin.id;
      open.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        clickMarker(candidate, pin.id);
      });

      const swatch = document.createElement('span');
      swatch.className = 'pin-swatch';
      swatch.dataset['digit'] = pin.digit ?? 'none';
      const label = document.createElement('span');
      label.className = 'pin-label';
      label.textContent = pin.digit ?? 'name this one';
      open.append(swatch, label);

      // THE WITNESS, as one glyph with its numbers on hover (§3 — evidence
      // beside the answer, never permission for it).
      const witness = witnessPin(pin, scan);
      const mark = document.createElement('span');
      let markTip: string;
      if (witness.kind === 'corroborated') {
        mark.className = 'pin-witness';
        mark.textContent = '✓';
        markTip = `Seen at ${witness.votes}/${scan.cleanResolutions.length} resolutions, ${(witness.distance * 1000).toFixed(1)} mm from the measured tip.`;
      } else {
        mark.className = 'pin-witness pin-witness-unsupported';
        mark.textContent = '⚠';
        markTip =
          `Nothing separated here (searched ${(witness.searchRadius * 1000).toFixed(0)} mm). ` +
          `A fused digit, a mitten, or a sixth finger — it will still graft.`;
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'pin-remove';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `remove ${pin.digit ?? 'unnamed'} pin`);
      remove.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        ui.set(candidate.handBone, withPins(state, removePin(state.pins, pin.id)));
        if (flyoutAt?.marker === pin.id) closeFlyout();
        render();
      });

      item.append(
        ...withTip(open, 'Rename this digit — the bar opens on its dot.'),
        ...withTip(mark, markTip),
        ...withTip(remove, 'Not a digit — throw this pin away.'),
      );
      void evidence;
      return item;
    }

    /**
     * The candidates nobody pinned. On the model they are discs a click away;
     * here they are one line, because making a person read a list of `tip-N`
     * rows is the eye-travel this round exists to remove. The × is the old "not
     * a digit" (Allen asked for it back): it throws a candidate out so it stops
     * cluttering the hand, and the count of what was thrown out stays visible so
     * an accident costs one click to undo.
     */
    function renderCandidates(
      candidate: HandCandidate,
      scan: TipConsensus,
      state: HandUiState,
      armed: boolean,
    ): HTMLElement {
      const wrap = document.createElement('div');
      wrap.className = 'hand-candidates';
      const ghosts = unpinnedClusters(state.pins, scan);
      const v = viewFor(candidate.handBone);

      if (armed && ghosts.length > 0) {
        const line = document.createElement('p');
        line.className = 'hand-candidate-line';
        line.textContent = `${ghosts.length} more on the model:`;
        for (const ghost of ghosts) {
          const chip = document.createElement('span');
          chip.className = v.hovered === ghost.id ? 'candidate-chip candidate-chip-hovered' : 'candidate-chip';
          chip.dataset['cluster'] = ghost.id;

          const adopt = document.createElement('button');
          adopt.type = 'button';
          adopt.className = 'candidate-adopt';
          adopt.dataset['cluster'] = ghost.id;
          adopt.textContent = ghost.id;
          // THE GHOST'S NUMBERS, said (§3B): the candidate-numbers list under Fine-tune is gone; a
          // person hovering this chip is exactly the person asking what the analyzer saw here.
          const numbers =
            `${ghost.id} · ${ghost.votes}/${scan.cleanResolutions.length} res${ghost.suspect ? ' · SUSPECT' : ''} · ` +
            `reach ${(ghost.reachFraction * 100).toFixed(0)}% · persists ${ghost.best.persistenceVoxels}`;
          chip.addEventListener('pointerenter', () => deps.say?.(numbers));
          chip.addEventListener('focusin', () => deps.say?.(numbers));
          adopt.addEventListener('pointerenter', () => setHovered(candidate, ghost.id));
          adopt.addEventListener('pointerleave', () => setHovered(candidate, null));
          adopt.addEventListener('click', (event) => {
            if (!event.isTrusted) return;
            clickMarker(candidate, ghost.id);
          });

          const reject = document.createElement('button');
          reject.type = 'button';
          reject.className = 'candidate-reject';
          reject.dataset['cluster'] = ghost.id;
          reject.textContent = '×';
          reject.setAttribute('aria-label', `reject ${ghost.id}`);
          reject.addEventListener('click', (event) => {
            if (!event.isTrusted) return;
            ui.set(candidate.handBone, withPins(state, rejectCluster(state.pins, ghost.id)));
            render();
          });
          chip.append(
            ...withTip(adopt, 'Pin this tip — the same as clicking its disc on the model.'),
            ...withTip(reject, 'Not a digit — hide this candidate. You can put it back.'),
          );
          line.append(chip);
        }
        wrap.append(line);
      }

      if (state.pins.rejected.length > 0) {
        const line = document.createElement('p');
        line.className = 'hand-rejected';
        line.textContent = `${state.pins.rejected.length} thrown out — `;
        for (const id of state.pins.rejected) {
          const restore = document.createElement('button');
          restore.type = 'button';
          restore.className = 'candidate-restore';
          restore.dataset['cluster'] = id;
          restore.textContent = `put ${id} back`;
          restore.addEventListener('click', (event) => {
            if (!event.isTrusted) return;
            ui.set(candidate.handBone, withPins(state, restoreCluster(state.pins, id)));
            render();
          });
          line.append(restore);
        }
        wrap.append(line);
      }
      return wrap;
    }

    function renderDeclare(candidate: HandCandidate, state: HandUiState): HTMLElement {
      const row = document.createElement('div');
      row.className = 'hand-declare';

      const graft = document.createElement('button');
      graft.type = 'button';
      graft.className = 'hand-graft';
      graft.textContent = 'Graft this hand';
      // THE BUTTON IS THE FALLBACK, not the path. Naming the last expected
      // digit grafts on its own (XD §5.6 gate 4); this is here for the hand
      // that has fewer digits than the ± says, or a person who wants to stop
      // early and graft what they have.
      graft.disabled = !canGraft(state);
      const blocked = graftBlockedReason(state);
      graft.addEventListener('click', (event) => {
        // THE MINT, when a person presses the button themselves. `confirmedBy`
        // is born from a gesture the browser vouched for and nowhere else —
        // a synthetic click gets `isTrusted === false` and signs nothing.
        if (!event.isTrusted) return;
        if (!canGraft(state)) return;
        runGraft(candidate, state);
      });
      row.append(...(blocked === null ? [graft] : withTip(graft, blocked)));

      // A disabled control must SAY why, visibly. A `title` is a tooltip nobody
      // hovers, which is how a person can answer one question, watch the button
      // stay dead, and have no way to learn that the other question exists.
      if (blocked !== null) {
        const why = document.createElement('p');
        why.className = 'hand-blocked';
        why.textContent = blocked;
        row.append(why);
      }
      return row;
    }

    render();
    // A GRAFT REPLACES THE SUBJECT, so the panel must re-read it. Without this
    // it kept offering a picker for a hand that already had fingers: the run
    // reported 64 bones and the panel still asked which branch was the thumb,
    // because it only re-rendered on its own state changes. A surface over a
    // replaceable subject has to subscribe to the replacement.
    const unsubscribe = store.subscribe(() => render());

    return {
      dispose() {
        unsubscribe();
        disposed = true;
        // The gesture is the panel's, and it must not outlive it: a pin tool
        // left armed would keep the crosshair and keep the pose gizmo withdrawn
        // on a panel that is no longer on screen.
        if (armedHand !== null) disarm();
        // AND SO MUST THE BAR. The panel is re-mounted whenever the workspace
        // re-lays-out — MEASURED at five times during boot alone — and the
        // flyout lives in `document.body`, outside the container the shell
        // clears. Without this, every remount leaks a dead bar: they stack up
        // invisible, they answer clicks into a closure whose panel is gone, and
        // the FIRST one in the DOM is the one a locator finds. That is exactly
        // how this was found — an e2e clicked a name on a bar belonging to a
        // panel that had not existed for several seconds, and nothing happened.
        // (XD §6.5, no dead chrome — here it was dead chrome that still ate
        // clicks.)
        flyout.dispose();
        // And the dots come off the character: they live on the BONES, so
        // nothing about tearing down this DOM would have removed them.
        deps.clearMarkers();
        container.replaceChildren();
        container.classList.remove('hands-panel');
      },
    };
  };
}
